<template>
  <div id="app" @dragenter="$event.stopPropagation()">
    <Header />
    <div class="layout">
      <carousel v-model="index" :per-page="1" :mouse-drag="false" :pagination-enabled="false">
        <slide>
          <CustomerGoal v-model="customerGoal" @change="next" />
        </slide>
        <slide v-if="customerGoal === 'pay'">
          <PayOps @back="prev" />
        </slide>
        <template v-else>
          <slide>
            <PersonType v-model="personType" @change="next" />
          </slide>
          <slide v-if="personType === 'PJ'">
            <RegisterJuridical v-model="company" @back="prev" @submit="next" />
          </slide>
          <template v-if="!authorization">
            <slide>
              <SlideRenderHelper name="register" :current="currentSlide">
                <Register
                  :person-type="personType"
                  v-model="owner"
                  @back="prev"
                  @email-verified="emailVerified = true"
                  @sms-verified="smsVerified = true"
                  @submit="next"
                />
              </SlideRenderHelper>
            </slide>
          </template>
          <slide>
            <SlideRenderHelper name="address" :current="currentSlide">
              <Address :person-type="personType" v-model="address" @back="prev" @submit="next" />
            </SlideRenderHelper>
          </slide>
          <slide>
            <SlideRenderHelper name="confirm_create" :current="currentSlide">
              <ConfirmAndCreate
                :partner-id="partnerId"
                :campaign-id="campaignId"
                :person-type="personType"
                :company="company"
                :owner="owner"
                :address="address"
                v-model="accountId"
                @back="prev"
                @next="next"
              />
            </SlideRenderHelper>
          </slide>
          <slide>
            <SlideRenderHelper name="confirm_email" :current="currentSlide">
              <ConfirmCode
                title="Confirmar Email"
                :account-id="accountId"
                v-model="resourceEmail"
                @change="confirmHandler"
              />
            </SlideRenderHelper>
          </slide>
          <slide>
            <SlideRenderHelper name="confirm_sms" :current="currentSlide">
              <ConfirmCode
                title="Confirmar SMS"
                :account-id="accountId"
                v-model="resourceSms"
                @change="confirmHandler"
              />
            </SlideRenderHelper>
          </slide>
          <slide>
            <SlideRenderHelper name="completed" :current="currentSlide">
              <Completed />
            </SlideRenderHelper>
          </slide>
        </template>
      </carousel>
    </div>
  </div>
</template>

<script>
import Header from '@/components/Header'
import CustomerGoal from '@/views/CustomerGoal'
import PersonType from '@/views/PersonType'
import Register from '@/views/Register'
import PayOps from '@/views/PayOps'
import RegisterJuridical from '@/views/RegisterJuridical'
import ConfirmAndCreate from '@/views/ConfirmAndCreate'
import Address from '@/views/Address'
import extractQueryParams from '@/common/utils/extractQueryParams'
import Completed from '@/views/Completed'
import ConfirmCode from '@/views/ConfirmCode'
import { toCamelCase } from '@/common/lib/case-parser'
import SlideRenderHelper from './components/SlideRenderHelper'

export default {
  components: {
    SlideRenderHelper,
    ConfirmCode,
    Completed,
    Address,
    ConfirmAndCreate,
    RegisterJuridical,
    PayOps,
    Register,
    PersonType,
    CustomerGoal,
    Header
  },
  data() {
    return {
      ready: false,
      authorization: '',
      customerGoal: '',
      personType: '',
      partnerId: null,
      campaignId: null,
      index: 0,
      owner: {},
      company: {},
      address: {},
      accountId: null,
      resourceEmail: {
        type: 'email',
        code: '',
        verified: false,
        resource: ''
      },
      resourceSms: {
        type: 'sms',
        code: '',
        verified: false,
        resource: ''
      }
    }
  },
  methods: {
    prev() {
      if (this.index === 3 && !!this.authorization) {
        return
      }
      this.index -= 1
    },
    next() {
      this.index += 1
    },
    confirmHandler({ verified }) {
      verified && this.next()
    },
    processQuery() {
      const { q } = extractQueryParams(document.URL)
      if (!q) {
        return
      }
      const data = toCamelCase(JSON.parse(atob(q)))

      if (data.campaignId) {
        this.campaignId = data.campaignId
      }

      if (data.user) {
        this.authorization = data.user
        this.personType = 'PJ'
        this.index = 2
        return
      }

      /* eslint-disable default-case */
      switch (data.origin) {
        case 'padmoney.com':
          this.accountId = data.accountId
          this.personType = data.personType
          this.partnerId = data.partnerId
          this.campaignId = data.campaignId

          Object.assign(data.personType === 'PJ' ? this.company : this.owner, {
            name: data.name,
            email: data.email
          })

          this.index = 2
          break
        case 'siena.padmoney.com':
          this.accountId = data.accountId
          this.resourceEmail.code = data.emailCode
          this.resourceEmail.resource = data.email
          this.resourceSms.resource = data.phone
          this.index = 5
          break
      }
    }
  },
  computed: {
    currentSlide() {
      const { customerGoal, personType } = this
      let { index } = this

      if (index === 0) {
        return 'customer_goal'
      }
      if (index === 1) {
        return customerGoal === 'pay' ? 'pay_ops' : 'person_type'
      }
      if (index === 2 && personType === 'PJ') {
        return 'register_juridical'
      }

      index -= Number(personType === 'PJ' && !this.authorization)
      if (index === 2) {
        return 'register'
      }
      if (index === 3) {
        return 'address'
      }
      if (index === 4) {
        return 'confirm_create'
      }
      if (index === 5) {
        return 'confirm_email'
      }
      if (index === 6) {
        return 'confirm_sms'
      }

      return 'completed'
    }
  },
  watch: {
    'owner.email': function (v) {
      this.resourceEmail.resource = v
    },
    'owner.phone': function (v) {
      this.resourceSms.resource = v
    }
  },
  mounted() {
    this.processQuery = this.processQuery.bind(this)
    this.processQuery()
  }
}
</script>

<style lang="scss">
$fa-font-path: '~/assets/fonts/fontawesome';

@import './assets/scss/main';

button:disabled {
  border-color: #f2f5fa !important;
  background-color: #f2f5fa !important;
  color: #5b5e63;
}
</style>
